import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Button, Box, IconButton, useMediaQuery, useTheme, Fade, Backdrop } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-scroll';
import logo from '../rooster.png';

const navItems = ['MUSIC', 'SHOWS', 'BAND', 'CONTACT'];

const Navbar = ({ showLogo = true }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [makeGrayscale, setMakeGrayscale] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    // Turn logo grayscale after 3 seconds (when animation completes)
    const timer = setTimeout(() => {
      setMakeGrayscale(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: isMobile ? 'rgba(10, 10, 10, 0.95)' : (scrolled ? 'rgba(10, 10, 10, 0.95)' : 'transparent'),
          backdropFilter: isMobile ? 'blur(10px)' : (scrolled ? 'blur(10px)' : 'none'),
          boxShadow: isMobile ? 3 : (scrolled ? 3 : 0),
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {showLogo && (
            <Link to="hero" spy={true} smooth={true} duration={800} style={{ cursor: 'pointer', background: 'transparent', display: 'inline-block', paddingLeft: '20px' }}>
              <Box
                component="img"
                src={logo}
                alt="Boxrooster"
                sx={{
                  height: { xs: '50px', md: '90px' },
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  filter: makeGrayscale ? 'grayscale(100%)' : 'none',
                  '&:hover': {
                    filter: 'grayscale(0%) brightness(1.2) saturate(1.3)',
                  },
                  transition: 'filter 0.5s',
                }}
              />
            </Link>
          )}

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                color: '#FFFFFF',
                '&:hover': { color: theme.palette.primary.main },
              }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navItems.map((item) => (
                <Link
                  key={item}
                  to={item.toLowerCase()}
                  spy={true}
                  smooth={true}
                  offset={-70}
                  duration={800}
                >
                  <Button
                    sx={{
                      color: '#FFFFFF',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      '&:hover': {
                        color: theme.palette.primary.light,
                        backgroundColor: 'transparent',
                      },
                      transition: 'color 0.3s',
                    }}
                  >
                    {item}
                  </Button>
                </Link>
              ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Modern Mobile Menu */}
      <Backdrop
        open={mobileOpen}
        onClick={handleDrawerToggle}
        sx={{
          zIndex: 1200,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
        }}
      />
      
      <Fade in={mobileOpen}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: '280px',
            backgroundColor: 'rgba(10, 10, 10, 0.98)',
            backdropFilter: 'blur(20px)',
            zIndex: 1300,
            display: mobileOpen ? 'flex' : 'none',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            pt: 12,
            gap: 2,
            borderLeft: `1px solid ${theme.palette.primary.main}33`,
          }}
        >
          {navItems.map((item, index) => (
            <Link
              key={item}
              to={item.toLowerCase()}
              spy={true}
              smooth={true}
              offset={-70}
              duration={800}
              onClick={handleDrawerToggle}
              style={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              <Box
                sx={{
                  color: '#FFFFFF',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  py: 1.5,
                  px: 3,
                  borderRadius: 1,
                  transition: 'all 0.3s',
                  animation: `fadeSlideIn 0.4s ease-out ${index * 0.1}s both`,
                  '@keyframes fadeSlideIn': {
                    from: {
                      opacity: 0,
                      transform: 'translateX(20px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateX(0)',
                    },
                  },
                  '&:hover': {
                    color: theme.palette.primary.light,
                    backgroundColor: `${theme.palette.primary.light}1A`,
                    transform: 'translateX(-5px)',
                  },
                }}
              >
                {item}
              </Box>
            </Link>
          ))}
        </Box>
      </Fade>
    </>
  );
};

export default Navbar;
